import { createSupabaseBrowserClient } from '@/client/supabase'
import { useQuery } from '@tanstack/react-query'
import * as Sentry from '@sentry/react-native'
import { Session, User } from '@supabase/supabase-js'

export const sessionKeys = {
  useSession: ['session'],
} as const

export type SessionData = {
  session: Session
  user: User
}

export const useClientSession = () => {
  const supabaseClient = createSupabaseBrowserClient()

  return useQuery<SessionData | null, Error>({
    queryKey: sessionKeys.useSession,
    queryFn: async () => {
      // Create a timeout promise that resolves to null after some seconds
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), 2500)
      })

      // Race between the session fetch and the timeout
      return Promise.race<SessionData | null>([
        (async () => {
          const {
            data: { session: existingSession },
            error,
          } = await supabaseClient.auth.getSession()

          if (!error && existingSession?.user) {
            const { id, email } = existingSession.user
            Sentry.setUser({ id, email })

            return {
              session: existingSession,
              user: existingSession.user,
            }
          }
          return null
        })(),
        timeoutPromise,
      ])
    },
    retry: false,
    staleTime: 0, // Always consider the session stale to ensure fresh data
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: true, // Refetch when window regains focus
  })
}
